import React, { useEffect, useState } from 'react';
import { Header, MobileBottomNav } from '../components/Layout';
import { referralsApi } from '../services/api';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { Gift, Copy, Share2, Trophy, Users as UsersIcon, Sparkles } from 'lucide-react';

const ReferralsPage = () => {
  const [data, setData] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      referralsApi.me().then((r) => setData(r.data)).catch(() => {}),
      referralsApi.leaderboard(10).then((r) => setLeaderboard(r.data || [])).catch(() => setLeaderboard([])),
    ]).finally(() => setLoading(false));
  }, []);

  const copy = async () => {
    if (!data) return;
    try {
      await navigator.clipboard.writeText(data.share_link);
      toast.success('Referral link copied!');
    } catch {
      toast.error('Copy failed — long-press to copy manually');
    }
  };

  const share = async () => {
    if (!data) return;
    const text = `Bet on anything with me on KrazyBets. Sign up with my link and we both get +50 Qbean tokens: ${data.share_link}`;
    if (navigator.share) {
      try { await navigator.share({ title: 'KrazyBets Referral', text, url: data.share_link }); return; } catch {}
    }
    copy();
  };

  if (loading) return (
    <div className="min-h-screen bg-[#1A1A1A] flex items-center justify-center">
      <div className="w-10 h-10 border-4 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="min-h-screen bg-[#1A1A1A] flex flex-col">
      <Header />
      <main className="flex-1 max-w-4xl mx-auto px-4 py-8 w-full pb-24 md:pb-8" data-testid="referrals-page">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 rounded-xl bg-[#D4AF37]/20 border border-[#D4AF37]/40 flex items-center justify-center">
            <Gift className="h-6 w-6 text-[#D4AF37]" />
          </div>
          <div>
            <h1 className="text-2xl font-black uppercase text-white" style={{ fontFamily: 'Outfit' }}>
              Refer & <span className="text-[#D4AF37]">Earn</span>
            </h1>
            <p className="text-gray-400 text-sm">Share your link. Earn 50 QBC per friend. They get 50 QBC too.</p>
          </div>
        </div>

        {data && (
          <>
            {/* Code & Link */}
            <div className="card-dark mb-6">
              <p className="text-xs uppercase text-gray-400 font-bold mb-2">Your unique referral code</p>
              <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
                <span className="font-mono text-3xl text-[#D4AF37] font-black tracking-widest" data-testid="referral-code">{data.code}</span>
                <div className="flex gap-2">
                  <Button onClick={copy} className="tab-3d text-white" data-testid="copy-referral">
                    <Copy className="h-4 w-4 mr-1" /> Copy
                  </Button>
                  <Button onClick={share} className="btn-primary" data-testid="share-referral">
                    <Share2 className="h-4 w-4 mr-1" /> Share
                  </Button>
                </div>
              </div>
              <div className="bg-[#0F0F0F] rounded-lg p-3 border border-[#3D3D3D] break-all text-sm text-gray-300 font-mono" data-testid="referral-link">
                {data.share_link}
              </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="card-dark text-center">
                <UsersIcon className="h-6 w-6 text-[#D4AF37] mx-auto mb-2" />
                <div className="text-3xl font-black text-white" data-testid="referred-count">{data.referred_count}</div>
                <div className="text-xs uppercase text-gray-400 font-bold">Friends Joined</div>
              </div>
              <div className="card-dark text-center">
                <Sparkles className="h-6 w-6 text-[#D4AF37] mx-auto mb-2" />
                <div className="text-3xl font-black text-[#D4AF37]" data-testid="referred-earned">{data.qbc_earned}</div>
                <div className="text-xs uppercase text-gray-400 font-bold">QBC Earned</div>
              </div>
            </div>

            {/* Referred list */}
            {data.referred_users.length > 0 && (
              <div className="card-dark mb-6">
                <h3 className="font-bold text-white mb-3">Your referrals</h3>
                <div className="space-y-2">
                  {data.referred_users.map((r) => (
                    <div key={r.id} className="flex items-center justify-between py-2 border-b border-[#3D3D3D] last:border-0 text-sm">
                      <div>
                        <span className="text-white font-semibold">{r.full_name}</span>
                        <span className="text-gray-500 text-xs ml-2">{r.country}</span>
                      </div>
                      <span className="text-xs text-gray-400">{(r.joined_at || '').slice(0, 10)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {/* Leaderboard */}
        <div className="card-dark">
          <h3 className="font-bold text-white mb-3 flex items-center gap-2">
            <Trophy className="h-5 w-5 text-[#D4AF37]" /> Top Referrers
          </h3>
          {leaderboard.length === 0 ? (
            <p className="text-gray-500 text-sm text-center py-6">Be the first to refer a friend and top this board!</p>
          ) : (
            <div className="space-y-2">
              {leaderboard.map((r, i) => (
                <div key={r.user_id} className="flex items-center justify-between py-2 border-b border-[#3D3D3D] last:border-0 text-sm">
                  <div className="flex items-center gap-3">
                    <span className={`w-7 h-7 rounded-full flex items-center justify-center font-bold text-xs ${i === 0 ? 'bg-[#D4AF37] text-black' : i === 1 ? 'bg-gray-300 text-black' : i === 2 ? 'bg-[#CD7F32] text-black' : 'bg-[#2D2D2D] text-gray-400'}`}>
                      {i + 1}
                    </span>
                    <span className="text-white font-semibold">{r.full_name}</span>
                    <span className="text-gray-500 text-xs">{r.country}</span>
                  </div>
                  <span className="text-[#D4AF37] font-bold">{r.referred_count} refs</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
      <MobileBottomNav />
    </div>
  );
};

export default ReferralsPage;
